import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Meme } from '../../data/memeData.ts';
import LazyImage from '../LazyImage.tsx';

interface MemeCardProps {
  meme: Meme;
}

const MemeCard: React.FC<MemeCardProps> = ({ meme }) => {
  const { i18n } = useTranslation();
  const isKorean = i18n.language === 'ko';

  const title = isKorean ? meme.titleKo : meme.title;
  const description = isKorean ? meme.descriptionKo : meme.description;

  return (
    <>
      {/* Mobile: Compact Card Style */}
      <Link
        to={`/memes/${meme.id}`}
        className="md:hidden group flex items-stretch bg-green-100 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-green-200"
        aria-label={`View ${title} meme`}
      >
        {/* Thumbnail - Left Side */}
        <div className="relative w-28 sm:w-32 flex-shrink-0 overflow-hidden">
          <LazyImage
            src={meme.thumbnail}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
          />
        </div>

        {/* Content - Right Side */}
        <div className="flex-1 p-3 sm:p-4 flex flex-col justify-center min-w-0 bg-green-100">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="flex-1 text-sm sm:text-base font-semibold text-gray-900 group-hover:text-green-600 transition-colors line-clamp-1">
              {title}
            </h3>
            <svg
              className="w-4 h-4 flex-shrink-0 text-gray-400 group-hover:text-green-500 transition-colors"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>

          <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">
            {description}
          </p>
        </div>
      </Link>

      {/* Desktop: Card Style */}
      <Link
        to={`/memes/${meme.id}`}
        className="hidden md:block group bg-green-100 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
      >
        {/* Meme Image */}
        <div className="relative aspect-square overflow-hidden">
          <LazyImage
            src={meme.thumbnail}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
          />
        </div>

        {/* Meme Info */}
        <div className="p-5 bg-green-100">
          <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors line-clamp-1">
            {title}
          </h3>
          <p className="text-gray-600 text-sm line-clamp-2">
            {description}
          </p>
        </div>
      </Link>
    </>
  );
};

export default MemeCard;
